import { BookingDetailsModal } from "@/components/Booking/modals/BookingDetailsModal"
import { Button } from "@/components/ui/button"
import { useBookingSync } from "@/hooks/useBookingSync"
import { useBookingAddMutation, useBookingQuery } from "@/query/bookingQuery"
import { useClientsQuery } from "@/query/clientQuery"
import { useHoursAvailableMutation } from "@/query/hoursAvailableQuery"
import { useKeysCountGetAllQuery } from "@/query/keysCountGet"
import { useServicesQuery } from "@/query/serviceQuery"
import { useShopDataQuery } from "@/query/shopDataQuery"
import {
  BookingListItem,
  ReservationCreateModal,
  Select,
} from "@okeys/shared-react-components"
import {
  BookingTable,
  formatPrestatereName,
  getPrestateres,
} from "@okeys/shared-utils"
import { ExclamationTriangleIcon } from "@radix-ui/react-icons"
import React, { useState } from "react"

type ExtendedReservationState =
  | BookingTable["state"]
  | "pending-past"
  | "pending-future"
  | "all"

export const Appointments: React.FC = () => {
  const {
    booking: bookings,
    pendingBooking: pendingReservation,
    errorBooking: errorReservation,
    refetchBooking: refetchReservation,
  } = useBookingQuery()

  const { shopData, pendingShopData, errorShopData } = useShopDataQuery()

  // Synchronisation en temps réel des bookings
  useBookingSync({
    shopId: shopData?.shopId || "",
    enabled: !!shopData?.shopId,
  })
  const { services, pendingServices, errorServices } = useServicesQuery()
  const {
    clients,
    isLoading: isLoadingClients,
    error: errorClients,
  } = useClientsQuery()
  const {
    keysCountItems,
    isLoading: isLoadingKeysCount,
    error: errorKeysCount,
  } = useKeysCountGetAllQuery()

  const [activeTab, setActiveTab] = useState<ExtendedReservationState>("all")
  const [selectedReservation, setSelectedReservation] =
    useState<BookingTable | null>(null)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false)

  // États pour la création de réservation et les créneaux disponibles
  const [availableSlots, setAvailableSlots] = useState<string[]>([])

  // Mutation pour récupérer les heures disponibles
  const hoursAvailableMutation = useHoursAvailableMutation()

  // Mutation pour l'ajout de réservation
  const bookingAddMutation = useBookingAddMutation()

  // Regroupement des états de chargement et d'erreur
  const isLoadingData =
    pendingReservation ||
    pendingShopData ||
    pendingServices ||
    isLoadingClients ||
    isLoadingKeysCount
  const hasDataErrors = !!(
    errorReservation ||
    errorShopData ||
    errorServices ||
    errorClients ||
    errorKeysCount
  )

  // Collecter toutes les erreurs
  const dataErrors = [
    errorReservation && "réservations",
    errorShopData && "données de la boutique",
    errorServices && "services",
    errorClients && "clients",
    errorKeysCount && "informations de fidélité",
  ].filter(Boolean)

  // Fonction pour déterminer si une réservation est passée
  const isPastReservation = (startTime: string | null | undefined) => {
    if (!startTime) return false
    return new Date(startTime) < new Date()
  }

  // Filtrer les réservations selon l'onglet actif
  const getFilteredReservations = () => {
    if (!bookings) return []

    return bookings.filter((reservation) => {
      if (activeTab === "all") return true
      if (activeTab === "validated") return reservation.state === "validated"
      if (activeTab === "cancelled") return reservation.state === "cancelled"
      if (activeTab === "absent") return reservation.state === "absent"
      if (activeTab === "pending-past") {
        return (
          reservation.state === "pending" &&
          isPastReservation(reservation.scheduledStart)
        )
      }
      if (activeTab === "pending-future") {
        return (
          reservation.state === "pending" &&
          !isPastReservation(reservation.scheduledStart)
        )
      }
      return false
    })
  }

  // Compter les réservations par statut
  const getCounts = () => {
    const counts = {
      all: bookings?.length || 0,
      "pending-future": 0,
      "pending-past": 0,
      validated: 0,
      cancelled: 0,
      absent: 0,
    }

    if (!bookings) return counts

    bookings.forEach((reservation) => {
      if (reservation.state === "validated") counts.validated++
      else if (reservation.state === "cancelled") counts.cancelled++
      else if (reservation.state === "absent") counts.absent++
      else if (reservation.state === "pending") {
        if (isPastReservation(reservation.scheduledStart)) {
          counts["pending-past"]++
        } else {
          counts["pending-future"]++
        }
      }
    })

    return counts
  }

  const counts = getCounts()
  const filteredReservations = getFilteredReservations()

  return (
    <div className="h-full flex flex-col gap-4">
      <div className="h-full flex flex-col gap-4">
        {/* Select for filtering appointments */}
        <div className="flex flex-row gap-2 justify-between items-center w-full">
          <Button variant="default" onClick={() => setIsCreateModalOpen(true)}>
            Ajouter un rendez-vous
          </Button>
          <div>
            <Select
              value={activeTab}
              onChange={(value) => {
                setActiveTab(value as ExtendedReservationState)
              }}
              options={[
                { value: "all", label: `Tous (${counts.all})` },
                {
                  value: "pending-future",
                  label: `À venir (${counts["pending-future"]})`,
                },
                {
                  value: "pending-past",
                  label: `En attente (${counts["pending-past"]})`,
                },
                {
                  value: "validated",
                  label: `Validés (${counts.validated})`,
                },
                {
                  value: "cancelled",
                  label: `Annulés (${counts.cancelled})`,
                },
                { value: "absent", label: `Absents (${counts.absent})` },
              ]}
              placeholder="Filtrer les rendez-vous"
              className="w-60"
            />
          </div>
        </div>

        {isLoadingData ? (
          <div className="space-y-4">
            <div className="flex items-center justify-center py-8">
              <div className="text-center space-y-2">
                <div className="inline-flex items-center gap-2">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
                  <span className="text-lg text-muted-foreground">
                    Chargement des données...
                  </span>
                </div>
                <div className="text-sm text-muted-foreground">
                  {pendingReservation && "Réservations • "}
                  {pendingShopData && "Boutique • "}
                  {pendingServices && "Services • "}
                  {isLoadingClients && "Clients • "}
                  {isLoadingKeysCount && "Fidélité"}
                </div>
              </div>
            </div>
          </div>
        ) : hasDataErrors && dataErrors.length > 0 ? (
          <div className="flex items-center gap-2 px-3 py-2 text-sm text-yellow-600 dark:text-yellow-400">
            <ExclamationTriangleIcon className="h-4 w-4" />
            <span>
              Certaines données non disponibles : {dataErrors.join(", ")}
            </span>
            {errorReservation && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => refetchReservation()}
                className="ml-auto h-7 text-xs"
              >
                Réessayer
              </Button>
            )}
          </div>
        ) : (
          <div className="flex-1 min-h-0 overflow-y-auto flex flex-wrap gap-4 content-start">
            {filteredReservations.length === 0 ? (
              <div className="text-center py-8 text-gray-500 dark:text-gray-400 w-full">
                Aucun rendez-vous dans cette catégorie
              </div>
            ) : (
              filteredReservations.map((reservation) => {
                // Construire les services à partir des données de réservation
                const reservationServices =
                  reservation.services?.map((service) => {
                    // Trouver les détails du service dans la liste des services
                    const serviceDetails = services?.find(
                      (s) => s.id === service.serviceId
                    )
                    return {
                      serviceId: service.serviceId,
                      name: service.name || serviceDetails?.name || "Service",
                      price: service.price || serviceDetails?.price || 0,
                      cancelled: service.cancelled || false,
                    }
                  }) || []

                // Obtenir le nom du prestataire en utilisant la nouvelle fonction
                const allPrestateres = getPrestateres(shopData)
                const prestataire = allPrestateres.find(
                  (p) => p.id === reservation.prestatereId
                )

                // Trouver le nom du client
                const client = clients?.find(
                  (c) => c.clientId === reservation.clientId
                )
                const clientName = client
                  ? `${client.name}`
                  : `Client #${reservation.clientId}`

                // Mapper vers le format BookingListItem
                const start = reservation.scheduledStart || ""
                const end = reservation.scheduledEnd || ""

                // Mapper le state pour BookingListItem (qui n'accepte pas "completed")
                const mappedState:
                  | "validated"
                  | "pending"
                  | "cancelled"
                  | "absent" =
                  reservation.state === "completed"
                    ? "validated"
                    : reservation.state

                const bookingData = {
                  bookingId: reservation.bookingId,
                  clientName: clientName,
                  startTime: start,
                  endTime: end,
                  date: start ? start.split("T")[0] : "",
                  services: reservationServices,
                  prestataireName: formatPrestatereName(prestataire),
                  totalPrice: reservation.total || 0,
                  keysUsed: 0,
                  state: mappedState,
                }

                return (
                  <BookingListItem
                    key={reservation.bookingId}
                    booking={bookingData}
                    onClick={() => {
                      setSelectedReservation(reservation)
                      setIsModalOpen(true)
                    }}
                  />
                )
              })
            )}
          </div>
        )}
      </div>

      {/* Modal de détails du booking */}
      <BookingDetailsModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        reservation={selectedReservation}
      />

      {/* Modal de création de réservation */}
      <ReservationCreateModal
        open={isCreateModalOpen}
        onOpenChange={(open) => {
          setIsCreateModalOpen(open)
          // Réinitialiser les états quand on ferme le modal
          if (!open) {
            setAvailableSlots([])
            hoursAvailableMutation.reset()
          }
        }}
        prestateres={getPrestateres(shopData).map((p) => ({
          id: p.id,
          givenName: p.givenName,
          email: p.email || "",
          familyName: p.familyName,
        }))}
        clients={
          clients?.map((c) => {
            // Trouver le nombre de keys pour ce client dans keysCountItems
            const keysCount = keysCountItems?.find(
              (k) => k.clientId === c.clientId
            )
            return {
              clientId: c.clientId,
              name: c.name || "",
              email: c.email || "",
              keys: keysCount?.keysNumber || undefined,
            }
          }) || []
        }
        services={services || []}
        promotions={[]} // TODO: Récupérer les promotions depuis une API
        onSubmit={async (data) => {
          try {
            // Vérifier que le prestataire est sélectionné
            if (!data.prestatereId || !data.startDate || !data.startTime) {
              console.error("Aucun prestataire sélectionné")
              return
            }

            const duration = data.selectedServices.reduce(
              (total, service) => total + (service?.duration || 60),
              0
            )

            const startDateStr = data.startDate.toISOString().split("T")[0]
            const startTime = `${startDateStr}T${data.startTime}:00`

            // Calculer l'heure de fin en ajoutant la durée (en minutes)
            const startDateTime = new Date(startTime)
            const endDateTime = new Date(
              startDateTime.getTime() + duration * 60000
            ) // durée en millisecondes
            const endTime = endDateTime.toISOString()

            // Construire l'objet de booking pour l'API
            const bookingData = {
              clientId: data.client.clientId,
              prestatereId: data.prestatereId,
              scheduledStart: startTime,
              scheduledEnd: endTime,
              services: data.selectedServices,
              shopId: shopData?.shopId || "",
            }

            await bookingAddMutation.mutateAsync(bookingData)
            setIsCreateModalOpen(false)
            refetchReservation()
          } catch (error) {
            console.error(
              "Erreur lors de la création de la réservation:",
              error
            )
          }
        }}
        availableSlots={availableSlots.map((hour) => ({
          time: hour,
          available: true,
        }))}
        loadingSlots={hoursAvailableMutation.isPending}
        onFetchSlots={(
          date: Date,
          prestatereId?: string,
          selectedServices?: Array<{ serviceId: string }>
        ) => {
          // Convertir la date en format YYYY-MM-DD
          const dateString = date.toISOString().split("T")[0]

          // Calculer la durée totale des services sélectionnés
          let totalDuration = 60 // Durée par défaut
          if (selectedServices && selectedServices.length > 0) {
            totalDuration = selectedServices.reduce((sum, service) => {
              const serviceDetails = services?.find(
                (s) => s.id === service.serviceId
              )
              return sum + (serviceDetails?.duration || 60)
            }, 0)
          }

          // Déclencher la mutation pour récupérer les créneaux
          if (prestatereId && shopData?.shopId) {
            hoursAvailableMutation.mutate(
              {
                prestatereId,
                date: dateString,
                duration: totalDuration.toString(),
                shopId: shopData.shopId,
              },
              {
                onSuccess: (hours) => {
                  setAvailableSlots(hours)
                },
                onError: (error) => {
                  console.error(
                    "Erreur lors de la récupération des créneaux:",
                    error
                  )
                  setAvailableSlots([])
                },
              }
            )
          }
        }}
      />
    </div>
  )
}
